/**
 * Configuring BroadPaper once, for the life of the process.
 *
 *     import { BroadPaper } from "@broadpaper/forme";
 *     BroadPaper.configure({ license: process.env.BROADPAPER_LICENSE });
 *
 * The licence is not a credential, so this is not a secrets API: it is the same
 * shape of thing as setting a locale. A browser bundle that carries the licence
 * string in plain text is fine — the string is a signed certificate, and
 * possessing it grants nothing beyond what its payload already says.
 *
 * Configuration is deliberately process-global rather than an argument to every
 * render. Threading a licence through `renderPdf`, the render service, the
 * batch endpoint, the .NET client and every future export path is exactly the
 * kind of plumbing that ends with one path quietly forgetting it, and a
 * customer who has paid discovering a watermark in production.
 *
 * The store hangs off a `Symbol.for` key rather than a module-level variable so
 * that two copies of this package in one dependency tree — the usual result of
 * a mixed-version install — still agree about the licence.
 */
import { unlicensed, verifyLicense, type VerifyOptions } from "./verify.js";
import type { LicenseStatus, TrustedKey } from "./types.js";

export interface BroadPaperConfig {
  /**
   * The licence string, `BP1.…`. Read from `BROADPAPER_LICENSE` when this is
   * not set and the runtime has environment variables.
   */
  license?: string | null;
  /**
   * Public keys to trust in addition to the ones built into the SDK. This is
   * how a development licence is honoured on a developer's machine without a
   * development key ever being trusted by a production build.
   */
  trustedKeys?: readonly TrustedKey[];
  /** Overrides the 30-day post-expiry grace period. Mostly for tests. */
  gracePeriodDays?: number;
  /**
   * Where licence messages go. Defaults to `console.warn` for warnings and
   * `console.info` for information; `null` silences both. The level is a
   * second argument so a logger written for earlier versions still works.
   */
  logger?: ((message: string, level: LogLevel) => void) | null;
}

/** How loudly a licence message is said. */
export type LogLevel = "info" | "warn";

interface Store {
  config: BroadPaperConfig;
  /** Memoised by the licence string that produced it. */
  cache: { key: string; status: Promise<LicenseStatus> } | null;
  /** Messages already logged, so a hundred renders do not print a hundred lines. */
  logged: Set<string>;
}

const STORE_KEY = Symbol.for("broadpaper.license.store");

function store(): Store {
  const g = globalThis as Record<symbol, unknown>;
  let s = g[STORE_KEY] as Store | undefined;
  if (!s) {
    s = { config: {}, cache: null, logged: new Set() };
    g[STORE_KEY] = s;
  }
  return s;
}

/**
 * Sets the licence and any other SDK-wide options. Call it once, at startup.
 *
 * Successive calls merge, so a host can set the licence in one place and the
 * grace period in another; passing `license: null` clears it.
 */
export function configure(config: BroadPaperConfig): void {
  const s = store();
  s.config = { ...s.config, ...config };
  s.cache = null;
  s.logged.clear();
}

/** What {@link configure} has been given, plus whatever the environment supplies. */
export function getConfig(): BroadPaperConfig {
  return { ...store().config };
}

/** Forgets the configuration entirely. Exists for tests. */
export function resetConfig(): void {
  const g = globalThis as Record<symbol, unknown>;
  delete g[STORE_KEY];
}

/**
 * `BroadPaper.configure({ … })`, for hosts that prefer a namespace to a bare
 * function. Both are the same store.
 */
export const BroadPaper = { configure, getConfig };

function env(name: string): string | undefined {
  // Node, Deno and bundlers that inline `process.env` all satisfy this; a
  // browser without a shim simply has no environment, which is not an error.
  const p = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
  const value = p?.env?.[name];
  return value && value.trim() ? value : undefined;
}

/**
 * Keys trusted in addition to the built-in ones, from configuration and from
 * `BROADPAPER_TRUSTED_KEYS` — a JSON array of `{ keyId, publicKey }`.
 *
 * A syntax error there is reported rather than swallowed: someone setting that
 * variable is doing so on purpose, and silently ignoring it would present as
 * "my development licence is not being honoured" with nothing to go on.
 */
function trustedKeysFromEnv(): readonly TrustedKey[] {
  const raw = env("BROADPAPER_TRUSTED_KEYS");
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) throw new Error("expected a JSON array");
    return parsed.map((entry, i) => {
      const k = entry as Record<string, unknown>;
      if (typeof k?.keyId !== "string" || typeof k?.publicKey !== "string") throw new Error(`entry ${i} needs keyId and publicKey`);
      return { keyId: k.keyId, publicKey: k.publicKey };
    });
  } catch (e) {
    warn(`BROADPAPER_TRUSTED_KEYS could not be read (${(e as Error).message}); it is being ignored.`);
    return [];
  }
}

/** Logs a message once, whatever it takes to reach the developer. */
export function warn(message: string): void {
  log(message, "warn");
}

/** Logs an informational message once. */
export function info(message: string): void {
  log(message, "info");
}

function log(message: string, level: LogLevel): void {
  const s = store();
  if (s.logged.has(message)) return;
  s.logged.add(message);
  const logger = s.config.logger;
  if (logger === null) return;
  const line = `[BroadPaper] ${message}`;
  if (logger) logger(line, level);
  else if (level === "info") console.info(line);
  else console.warn(line);
}

function report(status: LicenseStatus): void {
  if (status.message) warn(status.message);
  if (status.notice) info(status.notice);
}

export interface ResolveOptions extends VerifyOptions {
  /** A licence for this call only, overriding the configured one. */
  license?: string | null;
}

/**
 * The licence status for the current configuration.
 *
 * Memoised on the licence string, because every PDF asks and a signature check
 * per page of a five-hundred-page batch is waste. `configure` clears it, and a
 * per-call `license` bypasses it.
 *
 * Whatever the answer, any developer-facing message is logged once. That is the
 * only thing the SDK does that a caller did not ask for, and it is the
 * difference between a developer seeing "your licence expired" and a developer
 * seeing an unexplained watermark.
 */
export async function resolveLicenseStatus(options: ResolveOptions = {}): Promise<LicenseStatus> {
  const s = store();
  const config = s.config;
  const license = options.license !== undefined ? options.license : config.license ?? env("BROADPAPER_LICENSE") ?? null;
  const trustedKeys = [...(options.trustedKeys ?? []), ...(config.trustedKeys ?? []), ...trustedKeysFromEnv()];
  const gracePeriodDays = options.gracePeriodDays ?? config.gracePeriodDays;

  if (!license) {
    const status = unlicensed();
    report(status);
    return status;
  }

  const verify = (): Promise<LicenseStatus> =>
    verifyLicense(license, { ...(options.now ? { now: options.now } : {}), trustedKeys, ...(gracePeriodDays !== undefined ? { gracePeriodDays } : {}) }).then((status) => {
      report(status);
      return status;
    });

  // A fixed clock is a test or a deterministic render; caching across different
  // clocks would make the grace-period boundary depend on call order.
  if (options.now || options.license !== undefined) return verify();

  const key = `${license}|${trustedKeys.map((k) => k.keyId).join(",")}|${gracePeriodDays ?? ""}`;
  if (s.cache?.key !== key) s.cache = { key, status: verify() };
  return s.cache.status;
}
